import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cx } from './cx'
import { Pill, type Tone } from './Pill'
import s from './Steps.module.css'

/**
 * Where a step stands. The first three are the wizard's, derived from
 * `current` when a step names none. The other three arrived with the VERSION
 * TRACK (#3059) and are only ever SUPPLIED by the host — the kit decides no
 * state, compares no version and fetches nothing:
 *   · `active`  — the observed-current one (on a version track: running);
 *   · `ready`   — available, not running yet (a live update the loader can serve);
 *   · `waiting` — known, but behind something (the next install);
 *   · `unknown` — nothing reported. It never draws the done fill, so never green.
 */
export type StepState = 'done' | 'active' | 'upcoming' | 'ready' | 'waiting' | 'unknown'

export interface Step {
  key: string
  /** The short label on screen ("1.33.2"). */
  label: string
  /** optional short caption under the label */
  hint?: string
  /** mark this step complete regardless of position (a checkmark shows) */
  done?: boolean
  /** The host's own state for this step; wins over `current` and `done`. */
  state?: StepState
  /**
   * The EXACT full identity behind a short `label` (a full version with its
   * build, a solution's unique name): the step's tooltip and its accessible
   * name, while the short label stays the picture.
   */
  identity?: string
  /** Where the fact comes from, in the host's words ("reported by Elevate", "measured"). */
  provenance?: string
  /** The group (segment) this step belongs to — read only when `groups` is given. */
  group?: string
  /**
   * `gate`: a wall between two groups — an install the later steps wait
   * behind. Drawn as a BAR, never a dot. Read only when `groups` is given.
   */
  kind?: 'step' | 'gate'
}

/** A segment of a grouped track: a solution, or a module's own versions. */
export interface StepGroup {
  key: string
  /** The short name on screen ("Solution 1.33"). */
  label: string
  /** Its exact full identity — the tooltip and the accessible name. */
  identity?: string
  /** One host line beside the name ("first reported 12 Sep", "not installed"). */
  hint?: string
  /** Where that line comes from, in the host's words. */
  provenance?: string
  /**
   * `current` — the installed one: a recess. `next` — not installed yet: an
   * empty outline. `unknown` — nothing reported: the same empty outline,
   * never a fill. Default `current`.
   */
  state?: 'current' | 'next' | 'unknown'
}

/**
 * A horizontal stepper (the Kendo Stepper of the kit) — the progress spine of any
 * wizard: numbered nodes joined by a connector, with done / active / upcoming
 * states. Controlled: the parent owns `current` (index) and the step list; the
 * stepper renders the rail and (if `onStep` is given) lets the user jump to an
 * already-reached step. It's the chrome, not the flow — pair it with your own
 * body + Back/Next buttons (see the Setup wizard in the gallery).
 *
 * ── `compact` — the nodes without their words on screen (#2406) ───────────
 * A dot beside every label needs roughly 300px for three short steps, and a
 * phone dialog's header at 320px (the 200%-zoom equivalent) has about 230 once
 * it has a close button. The relationship workspace drew its own three
 * numbered dots and a "Step N of M" line for exactly that reason — a second
 * stepper that had no done state and nothing a screen reader could walk.
 *
 * Compact keeps the nodes, their states and the connector, and takes the
 * LABELS out of the picture but not out of the accessibility tree: each node
 * still reads "1 Relationship type", the current one still carries
 * `aria-current="step"`. The host says where the person is in words beside
 * the rail, because a caption is copy and copy is the host's.
 *
 * ── no `onStep`, no buttons ────────────────────────────────────────────────
 * A stepper nobody can navigate used to render every node as a DISABLED
 * `<button>`: three controls that exist only to be refused, announced as
 * "dimmed button", and counted by every target-size sweep as a 22px control
 * under the pointer floor (`relationship-picker-touch.spec.ts` test 9 found
 * exactly that the moment the workspace adopted this). `StatusRail` already
 * rules the other way — "absent ⇒ the node is a `<span>`, not a button. The
 * affordance is a prop." — and so does this now. With `onStep`, nodes are
 * buttons, and the ones not yet reached are disabled, as before.
 *
 * ── a horizontal rail that does not fit FITS ITSELF (#2406 b3-controls) ────
 * Every node kept its label on one line and nothing ever gave way, so six
 * steps drew a 666px rail in a 350px phone column: "Branding", "Modules" and
 * "Done" hung off the page (rogue census, control-clipped, /ui). The width a
 * rail needs is its words, which no stylesheet can know in advance, so the
 * rail MEASURES: when its content is wider than its box it draws the compact
 * nodes — every state, every connector, every label still its node's
 * accessible name — and keeps the CURRENT step's label on screen, because
 * without a host caption (which `compact` hosts supply) the words for where the
 * person is would otherwise be gone. It returns to the full rail as soon as
 * the box can hold the width it measured. `compact` and `vertical` rails are
 * left alone: the first already fits by choice, the second cannot overflow
 * sideways.
 *
 * ── `groups` — the VERSION TRACK (#3059) ───────────────────────────────────
 * Elevate ships two kinds of update: a LIVE update (the bundle the loader
 * serves by itself) and an INSTALL update (a new managed solution). Later
 * live updates WAIT behind the next install. That is still a progress spine —
 * steps in order, one where you are — so it is this component, not a new one:
 * with `groups`, consecutive steps of one `group` draw as a SEGMENT (a recess
 * with its name and line), a `kind: 'gate'` step draws as the WALL between two
 * segments (a bar — dot = live update, bar = install update), and every step
 * carries its state as a glyph AND a word (`stateLabels`, the host's words —
 * `StageLadder`'s #2710 rule: the kit bakes no language and throws on a
 * missing word). One group and no gate is a Marble module's own versions.
 * `compact` keeps the dots and the short labels for a row and takes the words
 * out of the picture, never out of the accessibility tree.
 *
 * PRESENTATION ONLY (ROOT review R7/R11.3): no version comparator, no policy,
 * no fetch. Which step is `waiting`, which gate is `done`, what a segment's
 * "first reported" line says — all of it is the host's data. A short label
 * keeps its exact `identity` in the tooltip and the accessible name, and
 * `provenance` says who reported each fact.
 */

/**
 * `true` while the rail's full, labelled drawing is wider than its box. The
 * natural width is recorded when it is measured, because once fitted the rail
 * is narrow and can no longer say how wide it would be. Re-measured from the
 * full drawing whenever the words change.
 */
function useFitted(enabled: boolean, words: string) {
  const rail = useRef<HTMLOListElement>(null)
  const [fitted, setFitted] = useState(false)
  const natural = useRef(0)
  const now = useRef(false)
  useLayoutEffect(() => {
    const el = rail.current
    now.current = false
    natural.current = 0
    setFitted(false)
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return
    const check = () => {
      if (!now.current) {
        if (el.scrollWidth > el.clientWidth + 1) {
          natural.current = el.scrollWidth
          now.current = true
          setFitted(true)
        }
      } else if (el.clientWidth >= natural.current) {
        now.current = false
        setFitted(false)
      }
    }
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled, words])
  return [rail, fitted] as const
}


function stateOf(steps: Step[], current: number, i: number): StepState {
  const own = steps[i]?.state
  if (own) return own
  return steps[i]?.done || i < current ? 'done' : i === current ? 'active' : 'upcoming'
}

/** A line is complete only into a reached step (done, or where the person is). */
const reached = (state: StepState) => state === 'done' || state === 'active'

/** The glyph in a node: ✓ done, ? unknown, otherwise the wizard's number (a track's bare dot passes none). */
function glyph(state: StepState, number?: number): ReactNode {
  return state === 'done' ? '✓' : state === 'unknown' ? '?' : number
}

export interface StepsProps {
  steps: Step[]
  /** The wizard's position. A grouped track whose steps all name their `state` passes `-1`. */
  current: number
  onStep?: (index: number) => void
  className?: string
  vertical?: boolean
  /** Nodes only on screen; every label stays in the accessible name. */
  compact?: boolean
  /**
   * #3059 — present ⇒ the VERSION TRACK: consecutive steps of one `group`
   * draw as a segment, `kind: 'gate'` steps as the walls between segments.
   */
  groups?: StepGroup[]
  /**
   * The word each state is announced with, from the host's localization.
   * REQUIRED for every state a grouped track draws — it throws, naming the
   * state, rather than draw a state by colour alone.
   */
  stateLabels?: Readonly<Partial<Record<StepState, string>>>
  /** The grouped track's accessible name ("Versions of Elevate in Contoso Production"). */
  label?: string
  testId?: string
}

export function Steps({
  steps, current, onStep, className, vertical = false, compact = false,
  groups, stateLabels, label, testId,
}: StepsProps) {
  const [rail, fitted] = useFitted(!groups && !vertical && !compact,
    steps.map((st) => `${st.label}\u0000${st.hint ?? ''}`).join('\u0001'))
  if (groups) {
    return (
      <Track steps={steps} current={current} groups={groups} stateLabels={stateLabels}
        label={label} compact={compact} className={className} testId={testId} />
    )
  }
  return (
    <ol ref={rail} className={cx(s.rail, vertical ? s.vertical : s.horizontal, (compact || fitted) && s.compact,
      fitted && s.fitted, className)} data-fitted={fitted || undefined} data-testid={testId}>
      {steps.map((st, i) => {
        const state = stateOf(steps, current, i)
        const reachable = !!onStep && (state === 'done' || i <= current)
        const face = (
          <>
            <span className={s.dot}>{glyph(state, i + 1)}</span>
            <span className={s.text}>
              <span className={s.label}>{st.label}</span>
              {st.hint && <span className={s.hint}>{st.hint}</span>}
            </span>
          </>
        )
        return (
          <li key={st.key} className={cx(s.step, s[state])} data-testid={`step-${st.key}`} data-state={state}
            aria-current={state === 'active' ? 'step' : undefined}>
            {onStep ? (
              <button type="button" className={s.node} disabled={!reachable}
                onClick={reachable ? () => onStep(i) : undefined}>
                {face}
              </button>
            ) : (
              <span className={s.node}>{face}</span>
            )}
            {i < steps.length - 1 && (
              /* COMPLETE only when BOTH ends are reached: this step is done and
                 the next one is done or current. A step marked `done` out of
                 order used to draw a finished line into a step nobody had
                 reached (#2406 kit-defects-b). */
              <span className={s.connector} aria-hidden="true"
                data-complete={state === 'done' && reached(stateOf(steps, current, i + 1)) ? '' : undefined} />
            )}
          </li>
        )
      })}
    </ol>
  )
}

/* ── the version track (#3059) ────────────────────────────────────────────── */

/**
 * The host's word for one state, or a named failure — `StageLadder`'s rule
 * (#2710): the kit carries no language, and a step with no word would be a
 * state told by colour alone. An untyped consumer is not stopped by a type.
 */
function stateWord(words: StepsProps['stateLabels'], state: StepState): string {
  const word: unknown = words?.[state]
  if (typeof word !== 'string' || word.trim().length === 0) {
    throw new Error(`Steps: stateLabels has no word for state '${state}'; a grouped track names every state it draws (#3059)`)
  }
  return word
}

/**
 * A short label on screen with its EXACT identity in the tooltip and the
 * accessible name. Without an identity the label is both.
 */
function Named({ text, identity, className, id }: { text: string; identity?: string; className?: string; id?: string }) {
  if (!identity || identity === text) return <span className={className} id={id}>{text}</span>
  return (
    <span className={className} id={id} title={identity}>
      <span aria-hidden="true">{text}</span>
      <span className={s.identity}>{identity}</span>
    </span>
  )
}

/** A gate's word is a status, so it is the kit's `Pill`; unknown never reads good. */
function gateTone(state: StepState): Tone {
  return state === 'done' ? 'good' : state === 'unknown' ? 'neutral' : 'warn'
}

type Run =
  | { kind: 'segment'; key: string; group?: StepGroup; items: { step: Step; state: StepState }[] }
  | { kind: 'gate'; step: Step; state: StepState }

type TrackProps = Required<Pick<StepsProps, 'steps' | 'current' | 'groups'>>
  & Pick<StepsProps, 'stateLabels' | 'label' | 'compact' | 'className' | 'testId'>

function Track({ steps, current, groups, stateLabels, label, compact, className, testId }: TrackProps) {
  if (!label) throw new Error('Steps: a grouped track needs a `label`, its accessible name (#3059)')
  // Consecutive steps of one group are one segment; a gate stands alone.
  const runs: Run[] = []
  steps.forEach((step, i) => {
    const state = stateOf(steps, current, i)
    if (step.kind === 'gate') { runs.push({ kind: 'gate', step, state }); return }
    const last = runs[runs.length - 1]
    if (last?.kind === 'segment' && last.items[0]?.step.group === step.group) { last.items.push({ step, state }); return }
    runs.push({
      kind: 'segment', key: step.group ?? step.key,
      group: groups.find((g) => g.key === step.group), items: [{ step, state }],
    })
  })
  return (
    <div className={cx(s.track, compact && s.compact, className)} role="group" aria-label={label} data-testid={testId}>
      <div className={s.lane}>
        {runs.map((run) => run.kind === 'gate'
          ? <Gate key={run.step.key} step={run.step} state={run.state} word={stateWord(stateLabels, run.state)} />
          : <Segment key={run.key} run={run} stateLabels={stateLabels} />)}
      </div>
    </div>
  )
}

/** One line of the host's words: its hint, then who reported it. */
function said(hint?: string, provenance?: string): string | undefined {
  return [hint, provenance].filter(Boolean).join(' · ') || undefined
}

function Segment({ run, stateLabels }: { run: Extract<Run, { kind: 'segment' }>; stateLabels: StepsProps['stateLabels'] }) {
  const group = run.group
  const headId = useId()
  const line = said(group?.hint, group?.provenance)
  return (
    <div className={s.segment} data-state={group?.state ?? 'current'} data-testid={`steps-group-${run.key}`}
      role="group" aria-labelledby={group ? headId : undefined}>
      {group && (
        <div className={cx(s.text, s.segHead)}>
          <Named text={group.label} identity={group.identity} className={s.segLabel} id={headId} />
          {line && <span className={s.hint}>{line}</span>}
        </div>
      )}
      <ol className={s.stops}>
        {run.items.map(({ step, state }) => {
          const word = stateWord(stateLabels, state)
          const tip = [step.identity ?? step.label, word, step.provenance].filter(Boolean).join(' · ')
          return (
            <li key={step.key} className={cx(s.stop, s[state])} data-testid={`step-${step.key}`} data-state={state}
              aria-current={state === 'active' ? 'step' : undefined} title={tip}>
              <span className={s.dot} aria-hidden="true">{glyph(state)}</span>
              <Named text={step.label} identity={step.identity} className={s.label} />
              <span className={s.text}>
                <span className={s.hint}>
                  <span className={s.word}>{word}</span>{step.hint ? ` · ${step.hint}` : null}
                </span>
                {step.provenance && <span className={s.hint}>{step.provenance}</span>}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function Gate({ step, state, word }: { step: Step; state: StepState; word: string }) {
  const line = said(step.hint, step.provenance)
  return (
    <div className={cx(s.gate, s[state])} data-state={state} data-testid={`step-${step.key}`}
      role="group" aria-label={`${step.identity ?? step.label} · ${word}`} title={step.identity}>
      <span className={s.gateBar} aria-hidden="true" />
      <span className={s.gateText}>
        <span className={s.label}>{step.label}</span>
        <Pill tone={gateTone(state)}>{word}</Pill>
        {line && <span className={cx(s.hint, s.text)}>{line}</span>}
      </span>
    </div>
  )
}
